import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import type { GridPaginationModel } from "@mui/x-data-grid";
import { listarPedidosLoja } from "services/lojaPedidos";
import type { IPedidoGerenciarListItem } from "interfaces/loja";
import { useDebouncedValue } from "./useDebouncedValue";
import { setMessage, useMaterialUIController } from "context";

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;

export type PedidosLojaFilters = {
  query: string;
  status: string;
  dataInicio: string;
  dataFim: string;
};

type PedidosLojaState = {
  filters: PedidosLojaFilters;
  page: number;
  pageSize: number;
};

const initialFilters: PedidosLojaFilters = {
  query: "",
  status: "",
  dataInicio: "",
  dataFim: "",
};

const initialState: PedidosLojaState = {
  filters: initialFilters,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

type Action =
  | { type: "SET_QUERY"; value: string }
  | {
      type: "SET_STABLE_FILTER";
      field: "status" | "dataInicio" | "dataFim";
      value: string;
    }
  | { type: "SET_PAGE"; page: number }
  | { type: "SET_PAGE_SIZE"; pageSize: number }
  | { type: "RESET_PAGE" };

function pedidosLojaReducer(
  state: PedidosLojaState,
  action: Action
): PedidosLojaState {
  switch (action.type) {
    case "SET_QUERY":
      return {
        ...state,
        filters: { ...state.filters, query: action.value },
      };
    case "SET_STABLE_FILTER":
      return {
        ...state,
        filters: { ...state.filters, [action.field]: action.value },
        page: 1,
      };
    case "SET_PAGE":
      if (action.page === state.page) return state;
      return { ...state, page: action.page };
    case "SET_PAGE_SIZE":
      if (action.pageSize === state.pageSize) return state;
      return {
        ...state,
        pageSize: action.pageSize,
        page: 1,
      };
    case "RESET_PAGE":
      return state.page === 1 ? state : { ...state, page: 1 };
    default:
      return state;
  }
}

export type PedidosLojaResumo = {
  receitaTotal: number;
  ticketMedio: number;
  quantidadePedidos: number;
};

export type UsePedidosLojaResult = {
  filters: PedidosLojaFilters;
  setStableFilter: (
    field: "status" | "dataInicio" | "dataFim",
    value: string
  ) => void;
  setQueryInput: (value: string) => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  rows: IPedidoGerenciarListItem[];
  total: number;
  resumo: PedidosLojaResumo | null;
  gridLoading: boolean;
  refetch: () => void;
  onPaginationModelChange: (model: GridPaginationModel) => void;
};

function buildListParams(state: PedidosLojaState, debouncedQuery: string) {
  return {
    page: state.page,
    limit: state.pageSize,
    status: state.filters.status || undefined,
    query: debouncedQuery.trim() || undefined,
    dataInicio: state.filters.dataInicio || undefined,
    dataFim: state.filters.dataFim || undefined,
  };
}

export function usePedidosLoja(): UsePedidosLojaResult {
  const [, dispatchCtx] = useMaterialUIController();
  const [state, dispatch] = useReducer(pedidosLojaReducer, initialState);

  const debouncedQuery = useDebouncedValue(
    state.filters.query,
    SEARCH_DEBOUNCE_MS
  );
  const prevDebouncedRef = useRef<string | null>(null);

  /** Após novo termo debounced, volta à página 1 antes do fetch (evita página obsoleta). */
  useLayoutEffect(() => {
    if (prevDebouncedRef.current === null) {
      prevDebouncedRef.current = debouncedQuery;
      return;
    }
    if (prevDebouncedRef.current === debouncedQuery) return;
    prevDebouncedRef.current = debouncedQuery;
    dispatch({ type: "RESET_PAGE" });
  }, [debouncedQuery]);

  const [rows, setRows] = useState<IPedidoGerenciarListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState<PedidosLojaResumo | null>(null);
  const [gridLoading, setGridLoading] = useState(true);
  /** Permite o botão "Pesquisar" forçar nova requisição com os mesmos filtros. */
  const [manualRefresh, setManualRefresh] = useState(0);

  const listParams = useMemo(
    () => buildListParams(state, debouncedQuery),
    [
      state.page,
      state.pageSize,
      state.filters.status,
      state.filters.dataInicio,
      state.filters.dataFim,
      debouncedQuery,
    ]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setGridLoading(true);
      try {
        // Listar todos os pedidos da loja paginados
        const { data, status: httpStatus } =
          await listarPedidosLoja(listParams);

        if (cancelled || httpStatus !== 200) return;
        setRows(data.pedidos ?? []);
        setTotal(data.total ?? 0);
        setResumo(data.resumo ?? null);
      } catch (error: unknown) {
        if (cancelled) return;
        const message =
          error instanceof Error ? error.message : "Falha ao listar pedidos.";
        setMessage(dispatchCtx, {
          open: true,
          severity: "error",
          text: message,
        });
      } finally {
        if (!cancelled) setGridLoading(false);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [listParams, dispatchCtx, manualRefresh]);

  const refetch = useCallback(() => {
    setManualRefresh((n) => n + 1);
  }, []);

  const setStableFilter = useCallback(
    (field: "status" | "dataInicio" | "dataFim", value: string) => {
      dispatch({ type: "SET_STABLE_FILTER", field, value });
    },
    []
  );

  const setQueryInput = useCallback((value: string) => {
    dispatch({ type: "SET_QUERY", value });
  }, []);

  const setPage = useCallback((page: number) => {
    dispatch({ type: "SET_PAGE", page });
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    dispatch({ type: "SET_PAGE_SIZE", pageSize });
  }, []);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const nextPage = model.page + 1;
      const nextSize = model.pageSize;
      if (nextSize !== state.pageSize) {
        dispatch({ type: "SET_PAGE_SIZE", pageSize: nextSize });
      } else if (nextPage !== state.page) {
        dispatch({ type: "SET_PAGE", page: nextPage });
      }
    },
    [state.page, state.pageSize]
  );

  return {
    filters: state.filters,
    setStableFilter,
    setQueryInput,
    page: state.page,
    pageSize: state.pageSize,
    setPage,
    setPageSize,
    rows,
    total,
    resumo,
    gridLoading,
    refetch,
    onPaginationModelChange,
  };
}
